import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type UIEvent,
} from "react"
import { api } from "../api/client"
import { useI18n } from "../hooks/useI18n"
import { useTelegram } from "../hooks/useTelegram"
import type { AppLocale } from "../i18n"
import {
  getMarketCountriesForPicker,
  MARKET_COUNTRIES,
  marketCountryPickerLabel,
  type MarketCountry,
  type MarketCountryCode,
} from "../i18n/countries"
import { type PlannedLanguageCode } from "../i18n/languages"
import { SPECIAL_UI } from "../i18n/specialLocales"
import { useAuthStore } from "../store/authStore"
import type { User } from "../types/api"
import { FigmaSignalGlyph } from "./FigmaSignalGlyphLibrary"
import { LanguageSelectionSheet } from "./LanguageSelectionSheet"
import "./CountrySelectionSheet.css"

const COUNTRY_FLAG_URLS = import.meta.glob<string>(
  "../assets/circle-flags/generated/*.svg",
  { eager: true, query: "?url", import: "default" },
)

type UserWithAppPreferences = User & {
  app_country_code?: string | null
  app_language_code?: string | null
}

const SAVE_ERROR: Record<AppLocale, string> = {
  non: SPECIAL_UI.non.preferences.saveError,
  sjn: SPECIAL_UI.sjn.preferences.saveError,
  "art-x-navi": SPECIAL_UI["art-x-navi"].preferences.saveError,
  "art-x-huttese": SPECIAL_UI["art-x-huttese"].preferences.saveError,
  "art-x-hv": SPECIAL_UI["art-x-hv"].preferences.saveError,
  az: "Parametrləri saxlamaq mümkün olmadı. Yenidən cəhd edin.",
  bn: "সেটিংস সংরক্ষণ করা যায়নি। আবার চেষ্টা করুন।",
  si: "සැකසුම් සුරැකීමට නොහැකි විය. නැවත උත්සාහ කරන්න.",
  sw: "Mipangilio haikuweza kuhifadhiwa. Jaribu tena.",
  ta: "அமைப்புகளைச் சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
  en: "Could not save the settings. Please try again.",
  ar: "تعذر حفظ الإعدادات. حاول مرة أخرى.",
  be: "Не ўдалося захаваць налады. Паспрабуйце яшчэ раз.",
  hy: "Չհաջողվեց պահպանել կարգավորումները։ Փորձեք կրկին։",
  id: "Pengaturan tidak dapat disimpan. Coba lagi.",
  ms: "Tetapan tidak dapat disimpan. Cuba lagi.",
  hi: "सेटिंग्स सहेजी नहीं जा सकीं। फिर से कोशिश करें।",
  it: "Impossibile salvare le impostazioni. Riprova.",
  ka: "პარამეტრების შენახვა ვერ მოხერხდა. სცადეთ ხელახლა.",
  kk: "Параметрлерді сақтау мүмкін болмады. Қайталап көріңіз.",
  ky: "Жөндөөлөрдү сактоо мүмкүн болгон жок. Кайра аракет кылыңыз.",
  fa: "تنظیمات ذخیره نشد. دوباره تلاش کنید.",
  "pt-BR": "Não foi possível salvar as configurações. Tente novamente.",
  ru: "Не удалось сохранить настройки. Попробуйте ещё раз.",
  es: "No se pudieron guardar los ajustes. Inténtalo de nuevo.",
  tg: "Танзимотро нигоҳ доштан муяссар нашуд. Боз кӯшиш кунед.",
  tl: "Hindi ma-save ang mga setting. Subukan muli.",
  tr: "Ayarlar kaydedilemedi. Tekrar deneyin.",
  uk: "Не вдалося зберегти налаштування. Спробуйте ще раз.",
  ur: "ترتیبات محفوظ نہیں ہو سکیں۔ دوبارہ کوشش کریں۔",
  uz: "Созламаларни сақлаб бўлмади. Қайта уриниб кўринг.",
  vi: "Không thể lưu cài đặt. Vui lòng thử lại.",
  de: "Die Einstellungen konnten nicht gespeichert werden. Bitte versuche es erneut.",
  fr: "Impossible d’enregistrer les paramètres. Veuillez réessayer.",
  ja: "設定を保存できませんでした。もう一度お試しください。",
  ko: "설정을 저장하지 못했습니다. 다시 시도해 주세요.",
  zh: "无法保存设置。请重试。",
  pl: "Nie udało się zapisać ustawień. Spróbuj ponownie.",
  nl: "De instellingen konden niet worden opgeslagen. Probeer het opnieuw.",
  th: "ไม่สามารถบันทึกการตั้งค่าได้ โปรดลองอีกครั้ง",
  cs: "Nastavení se nepodařilo uložit. Zkuste to znovu.",
  sv: "Inställningarna kunde inte sparas. Försök igen.",
  ro: "Setările nu au putut fi salvate. Încearcă din nou.",
}

function countryFlagUrl(country: MarketCountry): string {
  return COUNTRY_FLAG_URLS[`../assets/circle-flags/generated/${country.flagCode}.svg`] || ""
}

interface CountrySelectionSheetProps {
  title: string
  onBack: () => void
  onNext: (country: MarketCountry) => void
}

export function CountrySelectionSheet({
  title,
  onBack,
  onNext,
}: CountrySelectionSheetProps) {
  const { locale, direction, setLocale, t } = useI18n()
  const { tg, showAlert } = useTelegram()
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [hasMoreBelow, setHasMoreBelow] = useState(true)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [stage, setStage] = useState<"country" | "language">("country")
  const [draftCountryCode, setDraftCountryCode] = useState<MarketCountryCode | null>(null)
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<PlannedLanguageCode | null>(null)
  const [isSavingPreferences, setIsSavingPreferences] = useState(false)

  const countries = useMemo(
    () => getMarketCountriesForPicker(() => "", locale),
    [locale],
  )

  const selectedCountry = useMemo(
    () => MARKET_COUNTRIES.find((country) => country.code === draftCountryCode) || null,
    [draftCountryCode],
  )

  const canContinue = selectedCountry?.availability === "active"

  const updateScrollState = useCallback((element: HTMLDivElement) => {
    setIsScrolled(element.scrollTop > 0)
    setHasMoreBelow(element.scrollTop + element.clientHeight < element.scrollHeight - 1)
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    updateScrollState(viewport)
  }, [countries, updateScrollState])

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    updateScrollState(event.currentTarget)
  }

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const handleGrabberPointerDown = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleGrabberPointerMove = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finishGrabberDrag = (event: ReactPointerEvent<HTMLSpanElement>, allowClose: boolean) => {
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) onBack()
  }

  const handleSuggestOwn = useCallback(() => {
    const botUsername = (import.meta.env.VITE_BOT_USERNAME || "BeepAppBot").replace(/^@/, "")
    const url = `https://t.me/${botUsername}`
    if (tg?.openTelegramLink) tg.openTelegramLink(url)
    else window.open(url, "_blank", "noopener,noreferrer")
  }, [tg])

  const handleLanguageConfirmed = async (languageCode: PlannedLanguageCode) => {
    if (!selectedCountry || !canContinue || isSavingPreferences) return
    setIsSavingPreferences(true)
    try {
      const updatedUser = await api.patch<UserWithAppPreferences>("/auth/me/preferences", {
        country_code: selectedCountry.code,
        language_code: languageCode,
      })
      useAuthStore.setState({ user: updatedUser })
      setLocale(languageCode)
      setIsSavingPreferences(false)
      onNext(selectedCountry)
    } catch {
      setIsSavingPreferences(false)
      showAlert(SAVE_ERROR[locale] || SAVE_ERROR.en)
    }
  }

  if (stage === "language") {
    return (
      <LanguageSelectionSheet
        title={t("languageSelection.chooseLanguage")}
        selectedLanguageCode={selectedLanguageCode}
        isSaving={isSavingPreferences}
        onSelect={setSelectedLanguageCode}
        onSuggestOwn={handleSuggestOwn}
        onBack={() => setStage("country")}
        onNext={(language) => void handleLanguageConfirmed(language.code)}
      />
    )
  }

  return (
    <section
      className={`bbf-country-selection${isDragging ? " is-dragging" : ""}`}
      style={{ "--bb-country-drag-y": `${dragOffset}px` } as CSSProperties}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bbf-country-selection-title"
    >
      <span
        className="bbf-country-selection-grabber"
        aria-hidden="true"
        onPointerDown={handleGrabberPointerDown}
        onPointerMove={handleGrabberPointerMove}
        onPointerUp={(event) => finishGrabberDrag(event, true)}
        onPointerCancel={(event) => finishGrabberDrag(event, false)}
      />

      <h2 id="bbf-country-selection-title" className="bbf-country-selection-title" dir={direction}>{title}</h2>

      <div ref={viewportRef} className="bbf-country-selection-viewport" onScroll={handleScroll}>
        <div className="bbf-country-selection-list">
          {countries.map((country) => {
            const active = country.availability === "active"
            const selected = draftCountryCode === country.code
            const displayLabel = marketCountryPickerLabel(country)
            const flagUrl = countryFlagUrl(country)
            return (
              <button
                type="button"
                key={country.code}
                className={`bbf-country-choice${selected ? " is-selected" : ""}${active ? " is-available" : " is-unavailable"}`}
                aria-pressed={selected}
                onClick={() => {
                  setDraftCountryCode(country.code)
                }}
              >
                <span className="bbf-country-choice-flag" aria-hidden="true">
                  {flagUrl && <img src={flagUrl} alt="" />}
                </span>
                <span className="bbf-country-choice-label" dir={active ? "ltr" : "auto"}>{displayLabel}</span>
              </button>
            )
          })}
          <button
            type="button"
            className="bbf-country-choice suggest-own"
            onClick={handleSuggestOwn}
          >
            <span className="bbf-country-choice-flag bbf-country-choice-plus" aria-hidden="true">
              <FigmaSignalGlyph category="attention" slug="predlozhit-svoy-variant" />
            </span>
            <span className="bbf-country-choice-label" dir="ltr">Suggest your country</span>
          </button>
        </div>
      </div>

      {isScrolled && <span className="bbf-country-selection-fade top" aria-hidden="true" />}
      {hasMoreBelow && <span className="bbf-country-selection-fade bottom" aria-hidden="true" />}

      <div className="bbf-country-selection-actions">
        <button type="button" dir={direction} onClick={onBack}>{t("common.back")}</button>
        <button
          type="button"
          className="next"
          dir={direction}
          disabled={!canContinue}
          onClick={() => {
            if (!selectedCountry || !canContinue) return
            setSelectedLanguageCode(null)
            setStage("language")
          }}
        >
          {t("common.next")}
        </button>
      </div>
    </section>
  )
}
